<template>
  <v-card height="100%" class="d-flex flex-column" :loading="loading" flat color="bg1">
    <v-list-item two-line class="flex-none" :class="$vuetify.theme.isDark ? 'bg2' : 'white'">
      <v-list-item-content>
        <v-list-item-title class="title">Company Profile</v-list-item-title>
        <v-list-item-subtitle class="subtitle-1"> Update your profile </v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action>
        <v-btn small outlined color="bg2" @click="logout">
          <div class="error--text font-weight-bold text-capitalize">logout</div>
        </v-btn>
      </v-list-item-action>
    </v-list-item>
    <v-divider></v-divider>
    <v-form v-model="valid" class="flex-grow-1 overflow-auto">
      <v-sheet
        color="transparent"
        tile
        class="pa-4 d-flex flex-column"
        style="gap: 1.5rem">

        <v-sheet color="transparent" outlined class="rounded-lg d-flex flex-nowrap gap-4">
          <v-sheet outlined class="pa-4 rounded-lg d-flex flex-wrap w-100">
            <div class="pr-10" style="width: 230px">
              <v-list-item-title class="title">Company Photo</v-list-item-title>
              <v-list-item-subtitle style="white-space: normal">
                Please choose the appropriate image for your company
              </v-list-item-subtitle>
            </div>
            <v-sheet
              outlined
              style="border-color: #ccc !important"
              color="bg1"
              class="pa-4 rounded-lg flex-1"
              min-width="300px">
              <SingleImageUploader
                id="add-uploader"
                apiUrl="api/Companies/Image"
                v-model="selected.logoUrl"></SingleImageUploader>
            </v-sheet>
          </v-sheet>
          
          <v-sheet outlined class="pa-4 rounded-lg d-flex flex-wrap w-100">
            <div class="pr-10" style="width: 230px">
              <v-list-item-title class="title">Banner</v-list-item-title>
              <v-list-item-subtitle style="white-space: normal">
                Upload a banner image for your company
              </v-list-item-subtitle>
            </div>
            <v-sheet
              outlined
              style="border-color: #ccc !important"
              color="bg1"
              class="pa-4 rounded-lg flex-1"
              :class="{'disabled-sheet': !$has(permissions.CompaniesManagement.UICustomization)}"
              min-width="300px">
              <SingleImageUploader
                :disabled="!$has(permissions.CompaniesManagement.UICustomization)"
                id="banner-uploader"
                apiUrl="api/Companies/CustomUIImage"
                v-model="selected.customUI.bannerUrl"></SingleImageUploader>
            </v-sheet>
          </v-sheet>   
        </v-sheet>

        <v-sheet color="transparent" outlined class="rounded-lg d-flex flex-nowrap gap-4">
          <v-sheet outlined class="pa-4 rounded-lg d-flex flex-wrap w-100">
            <div class="pr-10" style="width: 230px">
              <v-list-item-title class="title">Client Logo</v-list-item-title>
              <v-list-item-subtitle style="white-space: normal">
                Upload your client's logo
              </v-list-item-subtitle>
            </div>
            <v-sheet
              outlined
              style="border-color: #ccc !important"
              color="bg1"
              :class="{'disabled-sheet': !$has(permissions.CompaniesManagement.UICustomization)}"
              class="pa-4 rounded-lg flex-1"
              min-width="300px">
              <SingleImageUploader
                :disabled="!$has(permissions.CompaniesManagement.UICustomization)"
                id="client-logo-uploader"
                apiUrl="api/Companies/CustomUIImage"
                v-model="selected.customUI.clientLogoUrl"></SingleImageUploader>
            </v-sheet>
          </v-sheet>
          
          <v-sheet outlined class="pa-4 rounded-lg d-flex flex-wrap w-100">
            <div class="pr-10" style="width: 230px">
              <v-list-item-title class="title">AVA Logo</v-list-item-title>
              <v-list-item-subtitle style="white-space: normal">
                Choose between white or colored AVA logo
              </v-list-item-subtitle>
            </div>
            <v-sheet
              outlined
              style="border-color: #ccc !important"
              color="bg1"
              class="pa-4 rounded-lg flex-1 d-flex align-center justify-center"
              :class="{'disabled-sheet': !$has(permissions.CompaniesManagement.UICustomization)}"
              min-width="300px">
              <div class="logo-selection-wrapper">
                <div 
                  class="logo-option"
                  :class="{'logo-option-selected': selected.customUI.avaLogoColor === LogoVariant.COLORED}" 
                  @click.stop="selectLogoType(LogoVariant.COLORED)"
                  role="radio"
                  :aria-checked="selected.customUI.avaLogoColor === LogoVariant.COLORED">
                  <div class="logo-preview primary-logo">
                    <v-img
                      :src="logoColored"
                      max-width="100"
                      max-height="50"
                      contain
                      class="logo-image"></v-img>
                  </div>
                  <v-icon 
                    color="success" 
                    class="logo-check-icon" 
                    v-if="selected.customUI.avaLogoColor === LogoVariant.COLORED">
                    mdi-check-circle
                  </v-icon>
                </div>
                
                <div
                  class="logo-option"
                  :class="{'logo-option-selected': selected.customUI.avaLogoColor === LogoVariant.WHITE}" 
                  @click.stop="selectLogoType(LogoVariant.WHITE)"
                  role="radio"
                  :aria-checked="selected.customUI.avaLogoColor === LogoVariant.WHITE">
                  <div class="logo-preview white-logo">
                    <v-img
                      :src="logoWhite"
                      max-width="100"
                      max-height="50"
                      contain
                      class="logo-image"></v-img>
                  </div>
                  <v-icon 
                    color="success" 
                    class="logo-check-icon"
                    v-if="selected.customUI.avaLogoColor === LogoVariant.WHITE">
                    mdi-check-circle
                  </v-icon>
                </div>
              </div>
            </v-sheet>
          </v-sheet>
        </v-sheet>
        
        <v-sheet outlined class="pa-4 rounded-lg d-flex flex-wrap">
          <div class="pr-10" style="width: 230px">
            <v-list-item-title class="title">Basic Information</v-list-item-title>
            <v-list-item-subtitle style="white-space: normal">
              Update company details here.
            </v-list-item-subtitle>
          </div>
          <v-sheet
            outlined
            style="border-color: #ccc !important"
            color="bg1"
            class="pa-4 rounded-lg flex-1"
            min-width="300px">
            <v-label class="black--text mb-2 d-block">Company Name</v-label>
            <v-text-field
              dense
              outlined
              :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
              v-model="selected.name"
              :rules="[formRules.required]"
              placeholder="Type Company Name"></v-text-field>
            <v-label class="black--text mb-2 d-block">Website</v-label>
            <v-text-field
              dense
              outlined
              :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
              v-model="selected.website"
              :rules="[formRules.url]"
              placeholder="Type Company Website"></v-text-field>
            <v-label class="black--text mb-2 d-block">Address</v-label>
            <v-text-field
              dense
              outlined
              :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
              v-model="selected.address.line1"
              placeholder="Company Address"></v-text-field>
            <v-row>
              <v-col>
                <v-label class="black--text mb-2 d-block">City</v-label>
                <v-text-field
                  dense
                  :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                  outlined
                  v-model="selected.address.city"
                  placeholder="City"></v-text-field>
              </v-col>
              <v-col>
                <v-label class="black--text mb-2 d-block">State</v-label>
                <v-text-field
                  dense
                  outlined
                  :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                  v-model="selected.address.state"
                  placeholder="State"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-label class="black--text mb-2 d-block">Country</v-label>
                <v-text-field
                  dense
                  outlined
                  :background-color="$vuetify.theme.isDark ? 'bg2' : 'white'"
                  v-model="selected.address.country"
                  placeholder="Country"></v-text-field>
              </v-col>
            </v-row>
          </v-sheet>
        </v-sheet>
        <div class="d-flex justify-end">
          <v-btn
            :disabled="!valid"
            large
            text
            color="primary"
            :loading="updateLoading"
            @click="updateCompany"
            >Save</v-btn
          >
        </div>
      </v-sheet>
    </v-form>
  </v-card>
</template>

<script>
  import permissions from '@Share/config/permissions';
  import formRules from '@Share/config/form-rules';
  import SingleImageUploader from '@Share/components/SingleImageUploader.vue';
  import { LogoVariant } from '@Share/config/enums';
  import { AVA_LOGOS } from '@Share/config/logos';
  const logoColored = AVA_LOGOS.castletonGreen;
  const logoWhite = AVA_LOGOS.white;
  import { $has, $handleError, $stripQueryParams } from '@/utils';
  import companyProfileService from '@Company/service/company-profile.service';

  export default {
    name: 'company-list',
    data() {
      return {
        logoColored,
        logoWhite,
        permissions,
        formRules,
        LogoVariant,
        selected: { 
          address: {},
          customUI: {
            bannerUrl: '',
            clientLogoUrl: '',
            avaLogoColor: LogoVariant.COLORED,
            techByLogoType: LogoVariant.WHITE
          },
        },
        showPassword: false,
        showCPassword: false,
        valid: false,
        loading: false,
        updateLoading: false,
      };
    },
    created() {
      let selected = this.$store.getters['ACCOUNT/GET_COMPANY'];
      this.loading = true;
      companyProfileService.getById(selected.id).then((res) => {
        // Convert API value to enum numeric value
        let logoType = res.data.customUI.avaLogoColor;
        if (logoType) {
          // If it's already a number, verify it's valid
          if (typeof logoType === 'number') {
            // Check if the number is a valid enum value
            if (![this.LogoVariant.COLORED, this.LogoVariant.WHITE].includes(logoType)) {
              logoType = this.LogoVariant.COLORED; // Fallback to default if invalid
            }
          } 
          // If it's a string, try to map it to our enum
          else if (typeof logoType === 'string') {
            // First try direct mapping ("COLORED" -> COLORED)
            const normalizedType = logoType.toUpperCase();
            if (this.LogoVariant[normalizedType] !== undefined) {
              logoType = this.LogoVariant[normalizedType];
            }
            // If that fails, try "colored" -> "COLORED" mapping
            else {
              logoType = this.LogoVariant.COLORED; // Default fallback
            }
          } else {
            logoType = this.LogoVariant.COLORED; // Fallback for any other type
          }
        } else {
          // Default value if not set
          logoType = this.LogoVariant.COLORED;
        }
        
        // Merge API data with default values to preserve UI state
        this.selected = {
          ...this.selected,
          ...res.data,
          customUI: {
            ...res.data.customUI,
            avaLogoColor: logoType
          }
        };
        this.loading = false;
      });
    },
    computed: {},
    methods: {
      $has,
      logout() {
        this.$store.dispatch('ACCOUNT/LOGOUT');
      },
      selectLogoType(type) {
        if (!this.$has(this.permissions.CompaniesManagement.UICustomization)) return;
        this.selected.customUI.avaLogoColor = type;
      },
      update(companyItemId = null) {
        this.$refs.editCompany.open(companyItemId, true);
      },
      del(id) {
        this.$dialog.error({
          text: 'Are You Sure You Want to Delete This Company?',
          title: 'Delete Company',
          actions: {
            false: 'Cancel',
            true: {
              text: 'Ok',
              color: 'primary',
              handle: () => {
                return this.$store.dispatch('COMPANY/DELETE', id);
              },
            },
          },
        });
      },
      updateCompany() {
        this.updateLoading = true;
        // Strip query params from image URLs before saving
        const dataToSave = {
          ...this.selected,
          logoUrl: $stripQueryParams(this.selected.logoUrl),
          customUI: {
            ...this.selected.customUI,
            bannerUrl: $stripQueryParams(this.selected.customUI?.bannerUrl),
            clientLogoUrl: $stripQueryParams(this.selected.customUI?.clientLogoUrl),
          }
        };
        companyProfileService.update(dataToSave)
          .then((res) => {
            // Update the account store with the new company data
            let user = this.$store.getters['ACCOUNT/GET_USER'];
            if (user.companyId === this.selected.id) {
              user.company = res.data;
              this.$store.commit('ACCOUNT/UPDATE_USER_BASIC', user);
            }
            this.$dialog.notify.success('Company Data Successfully Updated.');
          })
          .catch((err) => {
            $handleError(err);
          })
          .finally(() => {
            this.updateLoading = false;
          });
      },
    },

    components: {
      SingleImageUploader,
    },
  };
</script>

<style scoped>
.gap-4 > * + * {
  margin-left: 1rem;
}

.logo-selection-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.logo-option {
  position: relative;
  width: 180px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 16px;
  cursor: pointer;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-option:hover {
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.logo-option-selected {
  border: 2px solid var(--v-success-base);
  background-color: rgba(76, 175, 80, 0.05);
}

.logo-preview {
  width: 100%;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.white-logo {
  background-color: #c3c3c3;
  border: 1px solid #e0e0e0;
}

.primary-logo {
  background-color: white;
  border: 1px solid #e0e0e0;
}

.logo-label {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
  margin-bottom: 4px;
}

.logo-check-icon {
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 24px;
  filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1));
}

/* Dark theme support */
.v-application.theme--dark .logo-label {
  color: rgba(255, 255, 255, 0.8);
}

.v-application.theme--dark .primary-logo {
  background-color: #333;
  border-color: #444;
}

.v-application.theme--dark .white-logo {
  background-color: #424242;
  border-color: #555;
}

.v-application.theme--dark .logo-option {
  border-color: rgba(255, 255, 255, 0.1);
}

.v-application.theme--dark .logo-option-selected {
  background-color: rgba(76, 175, 80, 0.1);
}

@media (max-width: 600px) {
  .logo-selection-wrapper {
    justify-content: center;
  }
  
  .logo-option {
    width: 160px;
    padding: 12px;
  }
}

.disabled-sheet {
  opacity: 0.5;
  pointer-events: none;
}
</style>
